import { components } from "@/lib/openapi";
import { client } from "@/queries/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type FlightFeeFilter = components["schemas"]["FlightFeeFilter"];
export type SingleDayAggregateFees =
  components["schemas"]["SingleDayAggregateFees"];
export type MultiDayAggregateFees =
  components["schemas"]["MultiDayAggregateFees"];
export type FilterConfig = components["schemas"]["FilterConfig"];

// === Landing Fee Exception Types from OpenAPI ===
export type AirlineExceptionCriteria =
  components["schemas"]["AirlineExceptionCriteriaSchema"];
export type WeightExceptionCriteria =
  components["schemas"]["WeightExceptionCriteriaSchema"];
export type TailNumberExceptionCriteria =
  components["schemas"]["TailNumberExceptionCriteriaSchema"];
export type LandingFeeException =
  components["schemas"]["LandingFeeExceptionSchema"];
export type LandingFeeExceptionCreate =
  components["schemas"]["LandingFeeExceptionCreateSchema"];
export type LandingFeeExceptionUpdatePayload = LandingFeeExceptionCreate;

// New types for Aircraft and Aircraft Metadata
export type AircraftDatabase = components["schemas"]["AircraftDatabaseSchema"];
export type AircraftMetadata = components["schemas"]["AircraftMetadataSchema"];
export type AircraftMetadataCreate =
  components["schemas"]["AircraftMetadataCreateSchema"];

// Payload for notifying staff about aircraft metadata
export type NotifyStaffAircraftMetadataPayload =
  components["schemas"]["NotifyStaffAircraftMetadataSchema"];

// Tail Number Override Types
export type PricedFlightTailNumberOverride =
  components["schemas"]["PricedFlightTailNumberOverrideSchema"];
export type PricedFlightTailNumberOverrideCreate =
  components["schemas"]["PricedFlightTailNumberOverrideCreateSchema"];

interface QueryOptions {
  enabled?: boolean;
}

// Get all available dates with fee data
export function getAvailableDates() {
  return useQuery({
    queryKey: ["landing_dates"],
    queryFn: async ({ signal }) => {
      const { data, error } = await client
        .get()
        .GET("/api/landing/dates", { signal });
      if (data) {
        // Keep dates as-is from the server without timezone manipulation
        return data;
      }
      console.warn(error);
      throw new Error();
    },
  });
}

interface PricedFlightsOptions extends QueryOptions {
  feeDate: string;
  filter: FlightFeeFilter;
}

// Get priced flights for a specific date
export function getPricedFlights({
  feeDate,
  filter,
  enabled = false,
}: PricedFlightsOptions) {
  return useQuery({
    queryKey: ["priced_flights", feeDate, filter],
    queryFn: async ({ signal }) => {
      const { data, error } = await client
        .get()
        .POST("/api/landing/flights/{fee_date}", {
          params: {
            path: {
              fee_date: feeDate,
            },
          },
          body: { filter },
          signal,
        });
      if (data) return data;
      console.warn(error);
      throw new Error();
    },
    enabled,
  });
}

interface DailyReportOptions extends QueryOptions {
  date: string;
  filter?: FlightFeeFilter;
  export?: string;
}

// Get fee report for a single day
export function getDailyReport({
  date,
  filter,
  export: exportParam = undefined,
  enabled = false,
}: DailyReportOptions) {
  return useQuery({
    queryKey: ["daily_report", date, filter, exportParam],
    queryFn: async ({ signal }) => {
      const { data, error } = await client
        .get()
        .POST("/api/landing/report/day/{date}", {
          params: {
            path: {
              date,
            },
            query: exportParam ? { export: exportParam } : undefined,
          },
          body: filter,
          signal,
        });
      if (data) return data;
      console.warn(error);
      throw new Error();
    },
    enabled,
  });
}

interface RangeReportOptions extends QueryOptions {
  startDate: string;
  endDate: string;
  filter?: FlightFeeFilter;
}

// Get fee report for a date range
export function getRangeReport({
  startDate,
  endDate,
  filter,
  enabled = false,
}: RangeReportOptions) {
  return useQuery<MultiDayAggregateFees>({
    queryKey: ["range_report", startDate, endDate, filter],
    queryFn: async ({ signal }) => {
      const response = await client.get().POST("/api/landing/report/range", {
        params: {
          query: {
            start_date: startDate,
            end_date: endDate,
          },
        },
        body: filter,
        signal,
      });

      if (response.error) {
        console.warn(response.error);
        throw new Error("Failed to fetch report");
      }

      return response.data;
    },
    enabled,
    refetchOnWindowFocus: false,
  });
}

interface DownloadFlightsOptions extends QueryOptions {
  startDate: string;
  endDate: string;
  filter?: FlightFeeFilter;
}

// Download flights CSV for a date range
export function downloadFlightsCSV({
  startDate,
  endDate,
  filter,
  enabled = false,
}: DownloadFlightsOptions) {
  return useQuery<string>({
    queryKey: ["download_flights", startDate, endDate, filter],
    queryFn: async ({ signal }) => {
      const response = await client.get().POST("/api/landing/report/range", {
        params: {
          query: {
            start_date: startDate,
            end_date: endDate,
            export: "flights",
          },
        },
        body: filter,
        signal,
      });

      if (response.error) {
        console.warn(response.error);
        throw new Error("Failed to download flights");
      }

      return response.response.text();
    },
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

// Get all carriers that have landed at this airport
export function getCarriers(options?: QueryOptions) {
  return useQuery({
    queryKey: ["carriers"],
    queryFn: async ({ signal }) => {
      const { data, error } = await client
        .get()
        .GET("/api/landing/carriers", { signal });
      if (data) return data;
      console.warn("Failed to fetch carriers:", error);
      throw new Error("Failed to fetch carriers");
    },
    enabled: options?.enabled,
  });
}

interface FilterConfigOptions extends QueryOptions {
  startDate: string;
  endDate?: string;
}

// Get available filter options for the given date range
export function getFilterConfig({
  startDate,
  endDate,
  enabled = true,
}: FilterConfigOptions) {
  return useQuery<FilterConfig>({
    queryKey: ["filter_config", startDate, endDate],
    queryFn: async ({ signal }) => {
      const { data, error } = await client
        .get()
        .GET("/api/landing/filter_config", {
          params: {
            query: {
              start_date: startDate,
              end_date: endDate,
            },
          },
          signal,
        });
      if (data) return data;
      console.warn(error);
      throw new Error("Failed to fetch filter config");
    },
    enabled,
  });
}

// === New Query Functions ===

/**
 * Fetches all aircraft database records.
 */
export function getAircrafts(options?: QueryOptions) {
  return useQuery<AircraftDatabase[]>({
    queryKey: ["aircrafts"],
    queryFn: async ({ signal }) => {
      const { data, error } = await client
        .get()
        .GET("/api/landing/aircrafts", { signal });
      if (data) return data as AircraftDatabase[];
      console.warn("Failed to fetch aircrafts:", error);
      throw new Error("Failed to fetch aircrafts");
    },
    enabled: options?.enabled,
  });
}

/**
 * Fetches all aircraft metadata records.
 */
export function getAircraftMetadata(options?: QueryOptions) {
  return useQuery<AircraftMetadata[]>({
    queryKey: ["aircraft_metadata"],
    queryFn: async ({ signal }) => {
      const { data, error } = await client
        .get()
        .GET("/api/landing/aircraft_metadata", { signal });
      if (data) return data as AircraftMetadata[];
      console.warn("Failed to fetch aircraft metadata:", error);
      throw new Error("Failed to fetch aircraft metadata");
    },
    enabled: options?.enabled,
  });
}

// === Landing Fee Exception Queries/Mutations (using OpenAPI types) ===

const landingFeeExceptionQueryKey = ["landing_fee_exceptions"];

/**
 * Fetches all landing fee exceptions for the user's airport.
 */
export function useLandingFeeExceptions(options?: QueryOptions) {
  return useQuery<LandingFeeException[]>({
    queryKey: landingFeeExceptionQueryKey,
    queryFn: async ({ signal }) => {
      type ApiError = { detail?: string | string[] | object };

      const { data, error } = await client
        .get()
        .GET("/api/landing/exceptions", { signal });

      if (error) {
        console.error("Failed to fetch landing fee exceptions:", error);
        const apiError = error as ApiError;
        const detail = apiError?.detail ?? "Failed to fetch exceptions";
        throw new Error(
          typeof detail === "string" ? detail : JSON.stringify(detail)
        );
      }
      if (!data) {
        throw new Error("No data received for landing fee exceptions");
      }
      return data;
    },
    enabled: options?.enabled,
  });
}

/**
 * Mutation hook to create a new landing fee exception.
 */
export function useCreateLandingFeeException() {
  const queryClient = useQueryClient();
  type ApiError = { detail?: string | string[] | object };

  return useMutation<LandingFeeException, Error, LandingFeeExceptionCreate>({
    mutationFn: async (payload: LandingFeeExceptionCreate) => {
      const { data, error } = await client
        .get()
        .POST("/api/landing/exceptions", {
          body: payload,
        });

      if (error) {
        console.error("Failed to create landing fee exception:", error);
        const apiError = error as ApiError;
        let errorDetail = "Failed to create exception";
        if (apiError?.detail) {
          const detailValue = apiError.detail;
          errorDetail =
            typeof detailValue === "string"
              ? detailValue
              : Array.isArray(detailValue)
                ? detailValue.join(", ")
                : JSON.stringify(detailValue);
        }
        throw new Error(errorDetail);
      }
      if (!data) {
        throw new Error("No data received after creating exception");
      }
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: landingFeeExceptionQueryKey });
    },
    onError: (error) => {
      console.error("Mutation error:", error.message);
    },
  });
}

// === Landing Fee Exception Update Mutation ===

interface UpdateLandingFeeExceptionVariables {
  id: number;
  payload: LandingFeeExceptionUpdatePayload;
}

/**
 * Mutation hook to update an existing landing fee exception.
 */
export function useUpdateLandingFeeException() {
  const queryClient = useQueryClient();
  type ApiError = { detail?: string | string[] | object };

  return useMutation<
    LandingFeeException,
    Error,
    UpdateLandingFeeExceptionVariables
  >({
    mutationFn: async ({ id, payload }: UpdateLandingFeeExceptionVariables) => {
      const updatePayload = payload as LandingFeeExceptionUpdatePayload;
      const { data, error } = await client
        .get()
        .PUT("/api/landing/exceptions/{exception_id}", {
          params: {
            path: {
              exception_id: id,
            },
          },
          body: updatePayload,
        });

      if (error) {
        console.error(`Failed to update landing fee exception ${id}:`, error);
        const apiError = error as ApiError;
        let errorDetail = "Failed to update exception";
        if (apiError?.detail) {
          const detailValue = apiError.detail;
          errorDetail =
            typeof detailValue === "string"
              ? detailValue
              : Array.isArray(detailValue)
                ? detailValue.join(", ")
                : JSON.stringify(detailValue);
        }
        throw new Error(errorDetail);
      }
      if (!data) {
        throw new Error(`No data received after updating exception ${id}`);
      }
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: landingFeeExceptionQueryKey });
    },
    onError: (error) => {
      console.error("Update mutation error:", error.message);
    },
  });
}

// === Landing Fee Exception Toggle Status Mutation ===

interface ToggleLandingFeeExceptionStatusVariables {
  id: number;
}

/**
 * Mutation hook to toggle the status of a landing fee exception.
 */
export function useToggleLandingFeeExceptionStatus() {
  const queryClient = useQueryClient();
  type ApiError = { detail?: string | string[] | object };

  return useMutation<
    LandingFeeException,
    Error,
    ToggleLandingFeeExceptionStatusVariables
  >({
    mutationFn: async ({ id }: ToggleLandingFeeExceptionStatusVariables) => {
      const { data, error } = await client
        .get()
        .PATCH("/api/landing/exceptions/{exception_id}/toggle_status", {
          params: {
            path: {
              exception_id: id,
            },
          },
        });

      if (error) {
        console.error(`Failed to toggle status for exception ${id}:`, error);
        const apiError = error as ApiError;
        let errorDetail = "Failed to toggle exception status";
        if (apiError?.detail) {
          const detailValue = apiError.detail;
          errorDetail =
            typeof detailValue === "string"
              ? detailValue
              : Array.isArray(detailValue)
                ? detailValue.join(", ")
                : JSON.stringify(detailValue);
        }
        throw new Error(errorDetail);
      }
      if (!data) {
        throw new Error(
          `No data received after toggling status for exception ${id}`
        );
      }
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: landingFeeExceptionQueryKey });
    },
    onError: (error) => {
      console.error("Toggle status mutation error:", error.message);
    },
  });
}

// === Landing Fee Exception Delete Mutation ===

interface DeleteLandingFeeExceptionVariables {
  id: number;
}

/**
 * Mutation hook to delete a landing fee exception (sets status to DELETED).
 */
export function useDeleteLandingFeeException() {
  const queryClient = useQueryClient();
  type ApiError = { detail?: string | string[] | object };

  return useMutation<
    LandingFeeException,
    Error,
    DeleteLandingFeeExceptionVariables
  >({
    mutationFn: async ({ id }: DeleteLandingFeeExceptionVariables) => {
      const { data, error } = await client
        .get()
        .DELETE("/api/landing/exceptions/{exception_id}", {
          params: {
            path: {
              exception_id: id,
            },
          },
        });

      if (error) {
        console.error(`Failed to delete exception ${id}:`, error);
        const apiError = error as ApiError;
        let errorDetail = "Failed to delete exception";
        if (apiError?.detail) {
          const detailValue = apiError.detail;
          errorDetail =
            typeof detailValue === "string"
              ? detailValue
              : Array.isArray(detailValue)
                ? detailValue.join(", ")
                : JSON.stringify(detailValue);
        }
        throw new Error(errorDetail);
      }
      if (!data) {
        throw new Error(`No data received after deleting exception ${id}`);
      }
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: landingFeeExceptionQueryKey });
    },
    onError: (error) => {
      console.error("Delete mutation error:", error.message);
    },
  });
}

// === New Mutation for Aircraft Metadata ===

const aircraftMetadataQueryKey = ["aircraft_metadata"];

/**
 * Mutation hook to create a new aircraft metadata record.
 */
export function useCreateAircraftMetadata() {
  const queryClient = useQueryClient();
  type ApiError = { detail?: string | string[] | object };

  return useMutation<AircraftMetadata, Error, AircraftMetadataCreate>({
    mutationFn: async (payload: AircraftMetadataCreate) => {
      const { data, error } = await client
        .get()
        .POST("/api/landing/aircraft_metadata", {
          body: payload,
        });

      if (error) {
        console.error("Failed to create aircraft metadata:", error);
        const apiError = error as ApiError;
        let errorDetail = "Failed to create aircraft metadata";
        if (apiError?.detail) {
          const detailValue = apiError.detail;
          errorDetail =
            typeof detailValue === "string"
              ? detailValue
              : Array.isArray(detailValue)
                ? detailValue.join(", ")
                : JSON.stringify(detailValue);
        }
        throw new Error(errorDetail);
      }
      if (!data) {
        throw new Error("No data received after creating aircraft metadata");
      }
      return data as AircraftMetadata;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: aircraftMetadataQueryKey });
    },
    onError: (error) => {
      console.error("Create aircraft metadata mutation error:", error.message);
    },
  });
}

// === New Mutation for Notifying Staff about Aircraft Metadata ===

/**
 * Mutation hook to notify staff about an aircraft metadata record.
 */
export function useNotifyStaffAircraftMetadata() {
  type ApiError = { detail?: string | string[] | object };
  type NotifySuccessResponse = { message?: string };

  return useMutation<
    NotifySuccessResponse,
    Error,
    NotifyStaffAircraftMetadataPayload
  >({
    mutationFn: async (payload: NotifyStaffAircraftMetadataPayload) => {
      const { data, error } = await client
        .get()
        .POST("/api/landing/aircraft_metadata/notify_staff", {
          body: payload,
        });

      if (error) {
        const apiError = error as ApiError;
        let errorDetail = "Failed to send notification";
        if (apiError?.detail) {
          const detailValue = apiError.detail;
          errorDetail =
            typeof detailValue === "string"
              ? detailValue
              : Array.isArray(detailValue)
                ? detailValue.join(", ")
                : JSON.stringify(detailValue);
        }
        throw new Error(errorDetail);
      }
      if (!data) {
        return {
          message: "Notification sent, but no confirmation message received.",
        };
      }
      return data as NotifySuccessResponse;
    },
    onSuccess: (data) => {},
    onError: (error) => {},
  });
}

// === Tail Number Override Mutation ===

/**
 * Mutation hook to create or update a tail number override for a PricedFlight.
 */
export function useCreateOrUpdateTailNumberOverride() {
  const queryClient = useQueryClient();
  type ApiError = { detail?: string | string[] | object };

  return useMutation<
    PricedFlightTailNumberOverride,
    Error,
    PricedFlightTailNumberOverrideCreate
  >({
    mutationFn: async (payload: PricedFlightTailNumberOverrideCreate) => {
      const { data, error } = await client
        .get()
        .POST("/api/landing/tail_number_overrides", {
          body: payload,
        });

      if (error) {
        console.error("Failed to create/update tail number override:", error);
        const apiError = error as ApiError;
        let errorDetail = "Failed to create/update tail number override";
        if (apiError?.detail) {
          const detailValue = apiError.detail;
          errorDetail =
            typeof detailValue === "string"
              ? detailValue
              : Array.isArray(detailValue)
                ? detailValue.join(", ")
                : JSON.stringify(detailValue);
        }
        throw new Error(errorDetail);
      }
      if (!data) {
        throw new Error(
          "No data received after creating/updating tail number override"
        );
      }
      return data as PricedFlightTailNumberOverride;
    },
    onSuccess: (data) => {
      // Invalidate priced flights queries to reflect updated tail numbers
      queryClient.invalidateQueries({ queryKey: ["priced_flights"] });
    },
    onError: (error) => {
      console.error("Tail number override mutation error:", error.message);
    },
  });
}
